import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { GeneralLoading } from '@/components/GeneralStatus/GeneralLoading';
import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { SortDirectionDropdown } from '@/components/SortDirectionDropdown/SortDirectionDropdown';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TSegment } from '@/features/segments/types';
import { useBroadcastTableResponsiveBreakpoint } from '@/hooks/useBroadcastTableResponsiveBreakpoint';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

import { BroadcastScheduleProgramBodySegmentCardHeader } from './BroadcastScheduleProgramBodySegmentCardHeader';
import { BroadcastScheduleProgramBodySegmentCardList } from './BroadcastScheduleProgramBodySegmentCardList';

type TBroadcastScheduleProgramBodySegmentCardProps = {
  items: TSegment[];
  isLoading: boolean;
  segments?: boolean;
  /**
   * The emission being reviewed. Optional only because the page is still loading it;
   * every mount of this card is inside a review of one.
   */
  programBroadcast?: TProgramBroadcast;
};
export function BroadcastScheduleProgramBodySegmentCard({
  items,
  isLoading,
  segments = false,
  programBroadcast,
}: TBroadcastScheduleProgramBodySegmentCardProps) {
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const { underBroadcastTableResponsiveBreakpoint } = useBroadcastTableResponsiveBreakpoint();
  const {
    visibleBroadcastProgramCards,
    pinnedId,
    toolkitComponents,
    setVisibleBroadcastProgramCards,
  } = useBroadcastScheduleProgram();
  const { order_by, setFilters } = useProgramBroadcastFilters();
  const { t } = useTranslation();

  const allCardsVisible =
    visibleBroadcastProgramCards.segments &&
    visibleBroadcastProgramCards.segmentDetail &&
    pinnedId &&
    toolkitComponents[pinnedId];

  const sortDirection = order_by?.endsWith('asc') ? 'asc' : 'desc';

  const handleSortDirectionChange = (direction: string) => {
    setFilters({ order_by: `start_datetime ${direction}` });
  };

  return (
    <>
      {isLoading ? (
        <GeneralLoading />
      ) : (
        <Card
          classes={{
            rootSection: clsx(
              '!min-w-full w-full flex-auto order-3 lg:order-1 overflow-auto max-h-[600px] lg:max-h-full',
              {
                '!max-w-[290px] !min-w-0': allCardsVisible && underMinDesktopWith,
                'max-w-[25%]': allCardsVisible && !underMinDesktopWith,
              }
            ),
            container: 'py-size-4',
          }}
        >
          <GeneralHeader
            name={t('Franjas del programa')}
            onClose={() => {
              setVisibleBroadcastProgramCards(prev => {
                return {
                  ...prev,
                  segments: !prev.segments,
                };
              });
            }}
          />
          <div className="flex flex-col gap-2">
            <div
              className={clsx(
                'flex justify-between gap-2 pl-2',
                underBroadcastTableResponsiveBreakpoint
                  ? 'flex-col items-start'
                  : 'flex-row items-center'
              )}
            >
              <BroadcastScheduleProgramBodySegmentCardHeader
                className={clsx(underBroadcastTableResponsiveBreakpoint && 'w-full')}
                items={items}
                segments
              />
              <SortDirectionDropdown value={sortDirection} onChange={handleSortDirectionChange} />
            </div>
            <BroadcastScheduleProgramBodySegmentCardList
              items={items}
              segments={segments}
              programBroadcast={programBroadcast}
            />
          </div>
        </Card>
      )}
    </>
  );
}
